import { useQuery } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useState } from "react";
import { CreateWorkflowRuleModal } from "@/components/Organisms/CreateWorkflowRuleModal/CreateWorkflowRuleModal";
import { WorkflowRuleDetailDrawer } from "@/components/Organisms/WorkflowRuleDetailDrawer/WorkflowRuleDetailDrawer";
import { WorkflowRulesTable } from "@/components/Organisms/WorkflowRulesTable/WorkflowRulesTable";
import { EMPTY_ORG_ID } from "@/constants/org";
import { authQueryOptions } from "@/services/auth/auth.options";
import { useAssetTypeDrawerContext } from "@/utils/asset-type-detail/asset-type-drawer-context";

export function AdminAssetTypeRulesSection({
	stayOnRoute = false,
}: {
	stayOnRoute?: boolean;
}) {
	const { assetType } = useAssetTypeDrawerContext();
	const navigate = useNavigate();
	const rawSearch = useSearch({ strict: false }) as { ruleId?: string };
	const [localRuleId, setLocalRuleId] = useState<string | null>(null);
	const openRuleId = stayOnRoute
		? localRuleId
		: (rawSearch.ruleId ?? null);
	const [isModalOpen, setIsModalOpen] = useState(false);

	const lifecycleId = assetType.statusLifecycle?.id ?? null;

	const canCreateRuleQuery = useQuery(
		authQueryOptions.checkAction({
			name: "CreateWorkflowRule",
			entityType: "AssetType",
			entityId: assetType.id,
		}),
	);
	const canCreateRule = Boolean(canCreateRuleQuery.data);

	const canEditRuleQuery = useQuery(
		authQueryOptions.checkAction({
			name: "EditWorkflowRule",
			entityType: "WorkflowRule",
			entityId: openRuleId,
		}),
	);
	const canEditRule = Boolean(canEditRuleQuery.data);

	const canArchiveRuleQuery = useQuery(
		authQueryOptions.checkAction({
			name: "ArchiveWorkflowRule",
			entityType: "WorkflowRule",
			entityId: openRuleId,
		}),
	);
	const canArchiveRule = Boolean(canArchiveRuleQuery.data);

	const tableKey = `admin.configuration.assetTypes.${assetType.id}.workflowRules`;

	return (
		<div>
			<WorkflowRulesTable
				entityType="asset"
				entityTypeId={assetType.id}
				organizationId={EMPTY_ORG_ID}
				tableKey={tableKey}
				onCreateRuleClick={() => setIsModalOpen(true)}
				canCreateRule={canCreateRule}
				canEditRule={canEditRule}
				canArchiveRule={canArchiveRule}
				lifecycleId={lifecycleId}
				variant={stayOnRoute ? "drawer" : "page"}
				onRuleClick={(rule) => {
					if (stayOnRoute) {
						setLocalRuleId(rule.id);
						return;
					}
					void navigate({
						to: `/admin/configuration/assetTypes/${assetType.id}/full`,
						search: { tab: "rules", ruleId: rule.id },
					});
				}}
			/>

			<CreateWorkflowRuleModal
				isOpen={isModalOpen}
				setIsOpen={setIsModalOpen}
				handleClose={() => setIsModalOpen(false)}
				entityKind="AssetType"
				entityTypeId={assetType.id}
				mode="platform"
				organizationId={EMPTY_ORG_ID}
				lifecycleId={lifecycleId}
			/>

			<WorkflowRuleDetailDrawer
				ruleId={openRuleId}
				organizationId={EMPTY_ORG_ID}
				lifecycleId={lifecycleId}
				entityTypeId={assetType.id}
				mode="platform"
				canEdit={canEditRule}
				onClose={() => {
					if (stayOnRoute) {
						setLocalRuleId(null);
						return;
					}
					void navigate({
						to: `/admin/configuration/assetTypes/${assetType.id}/full`,
						search: { tab: "rules" },
						replace: true,
					});
				}}
				onFullView={(ruleId) => {
					void navigate({
						to: `/admin/configuration/assetTypes/${assetType.id}/full/rules/${ruleId}`,
					});
				}}
			/>
		</div>
	);
}
